import { useCallback, useEffect, useRef, useState } from 'react';

import type { ValueList, ValueLists } from '@ohpen/enso-ui';
import { ActionEntityType, Address, FieldType, firmsGetTradingAddresses, intermediaryGetAll, intermediaryGetById } from '@pmp/api';

import { INTERMEDIARY_TRADING_ADDRESSES_LIST } from '../brokers/tableConfig';
import { formatAddressAsLines } from '../firms';
import { ChangeLogDetail, ChangeLogTarget, ChangeLogValue } from './interfaces';

/** The list the log builds for itself to name the intermediaries a record points at. */
export const INTERMEDIARIES_LIST = 'changeLogIntermediaries';

/**
 * Fields that hold another record's id, by the list that names them in the log. They take precedence
 * over the change request screen's column list: a firm that is no longer a principal or a packager is
 * missing from those lists, but an old change still names it.
 */
export const REFERENCE_LIST_NAMES: Partial<Record<ActionEntityType, Record<string, string>>> = {
  [ActionEntityType.BROKERS]: { managerId: INTERMEDIARIES_LIST, subordinateIds: INTERMEDIARIES_LIST },
  [ActionEntityType.FIRMS]: {
    principalFirmIds: FieldType.FIRMS,
    packagerFirmIds: FieldType.FIRMS,
    packagerForFirmIds: FieldType.FIRMS,
    // Not an id: the selected operation types, named by the list the operations tab uses.
    operationsWhitelist: FieldType.OPERATION_TYPE,
  },
  [ActionEntityType.FIRM_PROCURATION_FEE_ASSIGNMENTS]: { firmId: FieldType.FIRMS },
};

const INTERMEDIARY_FIELDS = ['managerId', 'subordinateIds'];

const valuesOf = (detail: Record<string, ChangeLogDetail>, fields: string[]): string[] => {
  const values: ChangeLogValue[] = Object.values(detail).flatMap(({ changes }) =>
    changes.filter(change => fields.includes(change.field)).flatMap(change => [change.previousValue, change.newValue]),
  );

  return [...new Set(values.flat().filter((value): value is string => !!value))];
};

const loadTradingAddresses = async (firmId: string, signal: AbortSignal): Promise<ValueList> => {
  const { data } = await firmsGetTradingAddresses({ path: { firmId }, signal });

  // Inactive addresses stay in: a change can name an address the firm has since retired.
  return Object.fromEntries(
    (data ?? []).flatMap(address => (address.id ? [[address.id, address.name || formatAddressAsLines(address as Address)]] : [])),
  );
};

const loadIntermediaryNames = async (signal: AbortSignal): Promise<ValueList> => {
  const { data } = await intermediaryGetAll({ signal });

  return Object.fromEntries((data ?? []).flatMap(item => (item.id && item.displayName ? [[item.id, item.displayName]] : [])));
};

/**
 * The value lists that name the ids an intermediary's expanded changes hold: the trading addresses of
 * every firm those changes or the record name, and every intermediary. An address of a firm the
 * intermediary has left is only named once the change that moved them is expanded too. A lookup that
 * fails leaves the id showing and is tried again on the next expand.
 */
export const useReferenceLists = (target: ChangeLogTarget | null, detail: Record<string, ChangeLogDetail>): ValueLists => {
  const [lists, setLists] = useState<ValueLists>({});
  const requested = useRef(new Set<string>());
  const controller = useRef(new AbortController());

  const load = useCallback((key: string, listName: string, read: (signal: AbortSignal) => Promise<ValueList>) => {
    if (requested.current.has(key)) return;
    requested.current.add(key);
    const { signal } = controller.current;

    read(signal)
      .then(list => !signal.aborted && setLists(current => ({ ...current, [listName]: { ...current[listName], ...list } })))
      .catch(() => {
        if (!signal.aborted) requested.current.delete(key);
      });
  }, []);

  useEffect(() => {
    if (target?.entityType !== ActionEntityType.BROKERS) return;

    const firmIds = valuesOf(detail, ['firmId']);
    if (valuesOf(detail, ['tradingAddressId']).length) {
      firmIds.forEach(firmId =>
        load(`firm:${firmId}`, INTERMEDIARY_TRADING_ADDRESSES_LIST, signal => loadTradingAddresses(firmId, signal)),
      );
      // The firm the intermediary belongs to today, for a change of address that did not change the firm.
      load('record', INTERMEDIARY_TRADING_ADDRESSES_LIST, async signal => {
        const { data } = await intermediaryGetById({ path: { id: target.id }, signal });

        return data.firmId && !firmIds.includes(data.firmId) ? loadTradingAddresses(data.firmId, signal) : {};
      });
    }

    if (valuesOf(detail, INTERMEDIARY_FIELDS).length) load('intermediaries', INTERMEDIARIES_LIST, loadIntermediaryNames);
  }, [target, detail, load]);

  useEffect(
    () => () => {
      controller.current.abort();
      controller.current = new AbortController();
      requested.current.clear();
      setLists({});
    },
    [target],
  );

  return lists;
};
